import React, { createContext, useEffect, useState, useContext } from 'react'
import { ReportsWrapper, ReportsWrapperType } from '../wrapper'
import { ReportFormatter, ReportFilter } from '../query'
import ExtractedStringsFormatter from '../query/formatters/strings'
import { reportsService } from '../services'
import { applicationConfiguration, assetsContext } from '../../shared'
import { logger } from '../../../components'
import { unescapeObject, unescapeObjectKeys } from '../helpers'
import { ApplicationConfiguration, AssetsType } from '../../shared/types'
import { useReport } from '../hooks'
import { ForbiddenError } from 'app/modules/shared/exceptions'
import NotFoundPage from 'app/components/errors/not-found'
import { useDetectExport } from 'app/modules/shared/helpers'
import { AuthContext } from 'app/modules/user'
import { useExportProfile } from './export-profile-context'
import { ReportExportProfileOptions } from 'app/modules/user/features/profile/types'

export const reportContext = createContext<ReportsWrapperType>(undefined as any)

/**
 * Context for loading and providing report data
 */
export function WithReportContext({ children }: { children: JSX.Element }): JSX.Element | null {
  let timer: NodeJS.Timeout | null = null
  let fetching = false

  const { flowId, reportId, hash } = useReport()
  const { user } = useContext(AuthContext)
  const defaultReportsWrapper = new ReportsWrapper(undefined)
  const exporting = useDetectExport()
  const config = useContext(applicationConfiguration)
  const assets = useContext(assetsContext)
  const [error, setError] = useState<any>(null)
  const [forceRefresh, setForceRefresh] = useState<boolean>(false)
  const [reportsWrapper, setReportsWrapper] = useState<ReportsWrapperType>(defaultReportsWrapper)
  const { exportOptions } = useExportProfile()

  reportsWrapper.setRefreshReportCallback(() => {
    setForceRefresh(!forceRefresh)
  })

  async function fetchData() {
    if (fetching) return

    fetching = true

    try {
      const newReportsWrapper = await fetchReport(
        flowId,
        hash,
        reportId as string,
        config,
        exporting,
        user,
        assets,
        exportOptions
      )

      // Do not compare old data to new, but always assign new data
      // to state property, to be able to update scan progress bar constantly.
      setReportsWrapper(newReportsWrapper)
    } catch (error: any) {
      console.error(error)
      setError(error)
      timer && clearInterval(timer)
    } finally {
      fetching = false
    }
  }

  useEffect(() => {
    fetchData()
  }, [forceRefresh])

  useEffect(() => {
    const finish =
      (reportsWrapper.checkAllReportsReady() &&
        reportsWrapper.getFilesLoadingState() &&
        reportsWrapper.getAdditionalStepsState()) ||
      reportsWrapper.getJobError()

    if (!finish) {
      const isEmpty = reportsWrapper.isEmpty()
      const pause = isEmpty
        ? config.reports.queryInterval.initial
        : reportsWrapper.getDataPollPause() || config.reports.queryInterval.withData

      timer = setInterval(fetchData, pause * 1000)
    }

    return () => {
      timer && clearInterval(timer)
    }
  }, [flowId, reportsWrapper, config, hash, reportId, forceRefresh])

  if (error) {
    if (error.code === 404) {
      return <NotFoundPage />
    }
    if (error.code === 401) {
      throw new ForbiddenError()
    }

    throw error
  }

  if (!flowId && !hash) {
    return null
  }

  return <reportContext.Provider value={reportsWrapper}>{children}</reportContext.Provider>
}

/**
 * Fetch report from server and format it.
 * @param flowId
 */
async function fetchReport(
  flowId: string | undefined,
  hash: string | undefined,
  reportId: string,
  config: ApplicationConfiguration,
  exporting: boolean,
  user: any,
  assets: AssetsType,
  exportOptions: ReportExportProfileOptions | null
) {
  ExtractedStringsFormatter.profiles = user?.reportExportProfiles ?? []
  ExtractedStringsFormatter.exportOptions = exportOptions
  const formatter = new ReportFormatter(config, assets, exporting)
  const filterParams = getFilterParams()

  let report: any = null
  if (flowId) {
    report = await reportsService.getReports(flowId, filterParams)
  } else if (hash) {
    report = await reportsService.getReport(hash, reportId, filterParams)
  } else {
    return 'Invalid arguments'
  }

  const reportSnapshot = globalThis.structuredClone(report)
  logger.debug('Plain report: ', reportSnapshot)
  unescapeObjectKeys(report)
  unescapeObject(report)
  const formatted = formatter.format(report)
  logger.debug('Formatted report: ', formatted)

  const wrapped = new ReportsWrapper(formatted)

  return wrapped
}

/**
 * Turn filter object into query string
 */
function getFilterParams(): string {
  const filter = new ReportFilter()

  return filter.toQueryParams()
}
